// ============================================================================
// Plugin algèbre — recherche guidée par l'objectif (lot 2 de la refonte,
// docs/moteur/REFONTE_MOTEUR.md, « search »).
//
// Meilleur d'abord (coût du chemin + estimation propre à l'objectif), états
// dédoublonnés sur leur clé canonique, budget DUR (temps, nœuds, profondeur) avec
// un statut explicite quand il est épuisé. Les coups :
//   - règles de relation, la valeur à introduire étant proposée par l'énoncé
//     (retrancher le terme constant, diviser par le coefficient, chasser les
//     dénominateurs, multiplier par −1) ;
//   - identités appliquées à un sous-terme (dans le sens de l'objectif) ;
//   - « réduire » un membre (forme normale) et « produit nul » (coups du noyau,
//     prouvés exactement).
// Un coup dont la précondition est inconnue est joué SOUS CONDITION ('conditional')
// ou laissé aux branches ('branch') : la recherche repart alors sous chaque cas
// a > 0 | a < 0 | a = 0.
// ============================================================================

import { conditionOn, decide, factsFrom, type Facts } from '../../conditions';
import * as Q from '../../exact';
import {
  instantiateRelation,
  matchRelationFrom,
  newMatchCtx,
  rewriteSuccessors,
  ruleForms,
} from '../../match';
import { normalize, polyContent, polyRat, polyTerms, rfSimplify, rfToTerm } from '../../normal';
import type { LoadedRule } from '../../rules/loader';
import {
  canon,
  div,
  mapStatement,
  mul,
  neg,
  num,
  parseStatement,
  pow,
  relationsIn,
  statementKey,
  statementSymbols,
  statementText,
  substitute,
  symbolsOf,
  termKey,
  termText,
  type Rel,
  type Relation,
  type Sorts,
  type Statement,
  type Term,
} from '../../term';
import type { Budget, Condition, Goal, StepRelation } from '../../types';

export interface SearchStep {
  from: string;
  to: string;
  relation: StepRelation;
  /** règle appliquée ; absente pour un coup du noyau (réduire, produit nul) */
  ruleId?: string;
  move: string;
  conditions?: Condition[];
}

export type SearchResult =
  | { status: 'found'; steps: SearchStep[]; final: string; conditions: Condition[]; nodes: number }
  | { status: 'not_found'; nodes: number }
  | { status: 'budget_exhausted'; nodes: number; reason: 'ms' | 'nodes' }
  | { status: 'unsupported'; code: string }
  | {
      status: 'branches';
      branches: { when: Condition[]; result: SearchResult }[];
      nodes: number;
    };

export interface SearchOptions {
  rules: readonly LoadedRule[];
  goal: Goal;
  hypotheses?: readonly Condition[];
  sorts?: Sorts;
  budget: Budget;
  now?: () => number;
}

interface Node {
  s: Statement;
  key: string;
  g: number;
  h: number;
  depth: number;
  steps: SearchStep[];
  conditions: Condition[];
}

interface Ctx {
  rules: readonly LoadedRule[];
  goal: Goal;
  x: string | null;
  facts: Facts;
  hyps: Condition[];
  sorts: Sorts;
  deadline: number;
  now: () => number;
  /** paramètres dont le signe aurait permis un coup (branches à essayer) */
  wantsBranch: Set<string>;
}

/* ------------------------------ estimation ------------------------------ */

function size(t: Term): number {
  switch (t.k) {
    case 'num':
    case 'sym':
      return 1;
    case 'add':
    case 'mul':
      return 1 + t.c.reduce((s, c) => s + size(c), 0);
    case 'div':
    case 'pow':
    case 'binom':
      return 1 + size(t.a) + size(t.b);
    case 'fn':
      return 1 + size(t.a);
  }
}
const has = (t: Term, x: string): boolean => symbolsOf(t).has(x);
function occurrences(t: Term, x: string): number {
  switch (t.k) {
    case 'sym':
      return t.n === x ? 1 : 0;
    case 'num':
      return 0;
    case 'add':
    case 'mul':
      return t.c.reduce((s, c) => s + occurrences(c, x), 0);
    case 'div':
    case 'pow':
    case 'binom':
      return occurrences(t.a, x) + occurrences(t.b, x);
    case 'fn':
      return occurrences(t.a, x);
  }
}

/** Opérations appliquées à x dans une relation (0 : « x = … » sans x à droite). */
function isolationCost(r: Relation, x: string): number {
  const inL = has(r.l, x);
  const inR = has(r.r, x);
  if (!inL && !inR) return 0;
  let c = 0;
  if (inL && inR) c += 6;
  if (!inL) c += 1; // il faudra échanger les membres
  const side = inL ? r.l : r.r;
  c += size(side) - 1 + 2 * (occurrences(side, x) - 1) + 2 * fractionsWith(side, x);
  return c;
}

/** Fractions qui contiennent x (on préfère chasser les dénominateurs). */
function fractionsWith(t: Term, x: string): number {
  switch (t.k) {
    case 'div':
      return (has(t, x) ? 1 : 0) + fractionsWith(t.a, x) + fractionsWith(t.b, x);
    case 'add':
    case 'mul':
      return t.c.reduce((s, c) => s + fractionsWith(c, x), 0);
    case 'pow':
    case 'binom':
      return fractionsWith(t.a, x) + fractionsWith(t.b, x);
    case 'fn':
      return fractionsWith(t.a, x);
    default:
      return 0;
  }
}

function nf(t: Term, sorts: Sorts): Term | null {
  const n = normalize(t, { sorts });
  return n.ok ? rfToTerm(rfSimplify(n.rf)) : null;
}
function reduced(t: Term, sorts: Sorts): boolean {
  const n = nf(t, sorts);
  return n !== null && termKey(n) === termKey(canon(t, sorts));
}
function unexpanded(t: Term): number {
  let c = 0;
  const walk = (u: Term): void => {
    if (u.k === 'mul' && u.c.some((v) => v.k === 'add')) c++;
    if (u.k === 'pow' && u.a.k === 'add') c++;
    if (u.k === 'add' || u.k === 'mul') u.c.forEach(walk);
    else if (u.k === 'div' || u.k === 'pow') {
      walk(u.a);
      walk(u.b);
    } else if (u.k === 'fn') walk(u.a);
  };
  walk(t);
  return c;
}

function heuristic(s: Statement, ctx: Ctx): number {
  const g = ctx.goal.kind;
  if (s.k === 'expr') {
    const t = s.e;
    switch (g) {
      case 'factor':
        return t.k === 'mul' || t.k === 'pow' ? 0 : 1;
      case 'expand':
        return unexpanded(t) + (reduced(t, ctx.sorts) ? 0 : 1);
      case 'compute':
        return t.k === 'num' ? 0 : Math.max(1, size(t) - 1);
      default:
        return reduced(t, ctx.sorts) ? 0 : 1;
    }
  }
  if (!ctx.x) return 0;
  return relationsIn(s).reduce((sum, r) => sum + isolationCost(r, ctx.x!), 0);
}

function isGoal(s: Statement, ctx: Ctx): boolean {
  if (s.k === 'expr') {
    if (ctx.goal.kind === 'factor') {
      const t = s.e;
      if (t.k === 'pow') return t.a.k === 'add';
      if (t.k !== 'mul') return false;
      const nonconst = t.c.filter((c) => symbolsOf(c).size > 0);
      return nonconst.length >= 2 || nonconst.some((c) => c.k === 'add');
    }
    return heuristic(s, ctx) === 0;
  }
  if (!ctx.x || s.k === 'set') return false;
  const items = s.k === 'rel' ? [s as Relation] : s.items;
  return items.every(
    (r) =>
      r.l.k === 'sym' &&
      r.l.n === ctx.x &&
      !has(r.r, ctx.x!) &&
      (ctx.goal.kind !== 'solve' || reduced(r.r, ctx.sorts)),
  );
}

/* ------------------------------ coups ------------------------------ */

interface Move {
  s: Statement;
  step: Omit<SearchStep, 'from' | 'to'>;
  cost: number;
}

function addTerms(t: Term): Term[] {
  return t.k === 'add' ? t.c : [t];
}
function denominators(t: Term, acc: bigint[] = []): bigint[] {
  if (t.k === 'num' && t.v.d !== 1n) acc.push(t.v.d);
  if (t.k === 'div' && t.b.k === 'num' && Q.isInteger(t.b.v))
    acc.push(t.b.v.n < 0n ? -t.b.v.n : t.b.v.n);
  if (t.k === 'add' || t.k === 'mul') t.c.forEach((c) => denominators(c, acc));
  else if (t.k === 'div' || t.k === 'pow') {
    denominators(t.a, acc);
    denominators(t.b, acc);
  } else if (t.k === 'fn') denominators(t.a, acc);
  return acc;
}
const gcd = (a: bigint, b: bigint): bigint => (b === 0n ? a : gcd(b, a % b));

/** Valeurs à introduire proposées par l'énoncé, pour isoler x. */
function candidates(r: Relation, x: string): { add: Term[]; scale: Term[] } {
  const add: Term[] = [];
  const scale: Term[] = [];
  const inL = has(r.l, x);
  const xSide = inL ? r.l : r.r;
  const other = inL ? r.r : r.l;
  for (const t of addTerms(xSide)) if (!has(t, x) && addTerms(xSide).length > 1) add.push(neg(t));
  for (const t of addTerms(other)) if (has(t, x)) add.push(neg(t));
  // coefficient de x : diviser
  if (xSide.k === 'mul') {
    const coef = xSide.c.filter((c) => !has(c, x));
    if (coef.length) scale.push(coef.length === 1 ? coef[0]! : mul(...coef));
  }
  if (xSide.k === 'div' && !has(xSide.b, x)) scale.push(div(num(1), xSide.b));
  // dénominateurs numériques : les chasser
  const dens = denominators({ k: 'add', c: [r.l, r.r] });
  if (dens.length) {
    const lcm = dens.reduce((a, b) => (a / gcd(a, b)) * b, 1n);
    if (lcm > 1n) scale.push(div(num(1), num(Q.rat(lcm))));
  }
  return { add, scale };
}

/** Membre réécrit : forme normale si elle n'est pas plus grosse que l'écriture brute. */
function tidy(t: Term, sorts: Sorts): Term {
  const c = canon(t, sorts);
  const n = nf(c, sorts);
  return n && size(n) <= size(c) ? n : c;
}

/**
 * Ce que la forme de départ d'une règle exige d'être ÉCRIT dans la relation : ses fonctions
 * (√, exp, ln…) et ses puissances d'exposant numérique (« ?a² »). Sans ce filtre,
 * l'appariement inverse les nombres (4 = 2², 3 = √9) et la recherche explore des coups
 * absurdes (« x = 4 » → « 2 = √(x) »).
 */
interface WrittenNeeds {
  fns: Set<string>;
  pows: Set<string>;
}
function writtenNeeds(
  t: Term,
  acc: WrittenNeeds = { fns: new Set(), pows: new Set() },
): WrittenNeeds {
  switch (t.k) {
    case 'add':
    case 'mul':
      t.c.forEach((c) => writtenNeeds(c, acc));
      break;
    case 'div':
      writtenNeeds(t.a, acc);
      writtenNeeds(t.b, acc);
      break;
    case 'binom':
      // « k parmi n » (moteur v27) : une règle qui en écrit n'est essayée que sur une ligne qui en écrit
      acc.fns.add('binom');
      writtenNeeds(t.a, acc);
      writtenNeeds(t.b, acc);
      break;
    case 'pow':
      if (t.b.k === 'num') acc.pows.add(termKey(t.b));
      writtenNeeds(t.a, acc);
      writtenNeeds(t.b, acc);
      break;
    case 'fn':
      acc.fns.add(t.f);
      writtenNeeds(t.a, acc);
      break;
    default:
      break;
  }
  return acc;
}
/** variables de motif élevées à une puissance numérique (« ?a² ») */
function powBaseVars(t: Term, acc: Set<string> = new Set()): Set<string> {
  switch (t.k) {
    case 'add':
    case 'mul':
      t.c.forEach((c) => powBaseVars(c, acc));
      break;
    case 'div':
    case 'binom':
      powBaseVars(t.a, acc);
      powBaseVars(t.b, acc);
      break;
    case 'pow':
      if (t.a.k === 'sym' && t.b.k === 'num') acc.add(t.a.n);
      powBaseVars(t.a, acc);
      break;
    case 'fn':
      powBaseVars(t.a, acc);
      break;
    default:
      break;
  }
  return acc;
}
function relationHas(r: Relation, needs: WrittenNeeds): boolean {
  if (!needs.fns.size && !needs.pows.size) return true;
  const got = writtenNeeds({ k: 'add', c: [r.l, r.r] });
  return (
    [...needs.fns].every((f) => got.fns.has(f)) && [...needs.pows].every((e) => got.pows.has(e))
  );
}

function relationMoves(r: Relation, ctx: Ctx): { r: Relation; step: Move['step']; cost: number }[] {
  const out: { r: Relation; step: Move['step']; cost: number }[] = [];
  if (!ctx.x) return out;
  const x = ctx.x;
  const isEq = r.rel === '=';
  const { add, scale } = candidates(r, x);
  for (const rule of ctx.rules) {
    if (rule.appliesTo === 'expression') continue;
    if ((rule.appliesTo === 'equation') !== isEq) continue;
    const forms = ruleForms(rule);
    for (const d of rule.directions) {
      if (!d.goals.includes(ctx.goal.kind)) continue;
      const fFrom = forms[d.from]!;
      const fTo = forms[d.to]!;
      const fromSides: Term = { k: 'add', c: fFrom.sides as Term[] };
      if (!relationHas(r, writtenNeeds(fromSides))) continue;
      // « ?a² = ?b » : la grandeur élevée au carré est celle qu'on cherche (jamais 9 = 3²)
      const squared = [...powBaseVars(fromSides)];
      const mctx = newMatchCtx(rule, ctx.sorts, 400);
      for (const b0 of matchRelationFrom(fFrom, r, mctx)) {
        if (squared.some((v) => b0[v] && !has(b0[v]!, x))) continue;
        const choices: Record<string, Term>[] = [];
        if (!d.introduces.length) choices.push({});
        else if (d.introduces.length === 1) {
          const name = d.introduces[0]!;
          // une règle « ajouter » prend les termes, une règle « multiplier/diviser » les facteurs
          const toText = rule.forms[d.to]!.text;
          // `scale` : valeurs par lesquelles DIVISER (le coefficient de x, 1/6 pour chasser des sixièmes)
          const pool = toText.includes('+')
            ? add
            : toText.includes('÷')
              ? scale
              : scale.map(invert);
          for (const v of pool) choices.push({ [name]: v });
          if (toText.includes('×') && !toText.includes('+')) choices.push({ [name]: num(-1) });
        }
        for (const ch of choices) {
          const b = { ...b0, ...ch };
          const inst = instantiateRelation(fFrom, fTo, r, b, ctx.sorts);
          if (!inst) continue;
          // préconditions
          const conds = rule.preconditions.map((c) => ({
            ...c,
            of: c.of.map((n) => termText(tidy(b[n] ?? num(0), ctx.sorts))),
          }));
          const ds = conds.map((c) => decide(c, ctx.facts));
          if (ds.includes('false')) continue;
          const open = conds.filter((_, i) => ds[i] !== 'true');
          if (open.length) {
            // une condition sur l'inconnue n'est jamais une hypothèse
            if (open.some((c) => c.of.some((t) => mentions(t, x)))) continue;
            if ((rule.onUnknown ?? 'conditional') === 'branch') {
              open.forEach((c) => c.of.forEach((t) => ctx.wantsBranch.add(t)));
              continue;
            }
            if (rule.onUnknown === 'refuse') continue;
          }
          const next: Relation = {
            rel: inst.rel,
            l: tidy(inst.l, ctx.sorts),
            r: tidy(inst.r, ctx.sorts),
          };
          out.push({
            r: next,
            step: {
              relation: rule.relation,
              ruleId: rule.qid,
              move: rule.id,
              ...(open.length && { conditions: open }),
            },
            cost: Math.max(1, rule.cost) + 1,
          });
        }
      }
    }
  }
  return out;
}
function mentions(text: string, x: string): boolean {
  const p = parseStatement(text);
  return p.ok && statementSymbols(p.s).has(x);
}
function invert(t: Term): Term {
  return t.k === 'div' && t.a.k === 'num' && Q.eq(t.a.v, Q.ONE) ? t.b : div(num(1), t);
}

function expressionMoves(
  t: Term,
  ctx: Ctx,
  goal: string,
): { t: Term; step: Move['step']; cost: number }[] {
  const out: { t: Term; step: Move['step']; cost: number }[] = [];
  for (const rule of ctx.rules) {
    if (rule.appliesTo !== 'expression') continue;
    const forms = ruleForms(rule);
    for (const d of rule.directions) {
      if (!d.goals.includes(goal as never)) continue;
      const mctx = newMatchCtx(rule, ctx.sorts, 400);
      for (const r of rewriteSuccessors(forms[d.from]!, forms[d.to]!, t, mctx)) {
        const conds = rule.preconditions.map((c) => ({
          ...c,
          of: c.of.map((n) => termText(r.bindings[n] ?? num(0))),
        }));
        const ds = conds.map((c) => decide(c, ctx.facts));
        if (ds.includes('false')) continue;
        const open = conds.filter((_, i) => ds[i] !== 'true');
        out.push({
          t: r.term,
          step: {
            relation: rule.relation,
            ruleId: rule.qid,
            move: rule.id,
            ...(open.length && { conditions: open }),
          },
          cost: Math.max(1, rule.cost) + 1,
        });
      }
    }
  }
  // factoriser par le facteur commun (coefficients et lettres) : « 6x + 4 = 2(3x + 2) »
  if (goal === 'factor' || goal === 'solve') {
    const f = factorOut(t, ctx.sorts);
    if (f)
      out.push({
        t: f,
        step: { relation: 'identity', move: 'factor_common', ...ruleOf(ctx, 'algebra.distribute') },
        cost: 2,
      });
  }
  // réduire / calculer (forme normale) : coup du noyau, prouvé exactement ; en dernier recours
  const n = nf(t, ctx.sorts);
  const base = goal === 'compute' ? t : canon(t, ctx.sorts);
  if (n && termKey(n) !== termKey(base) && goal !== 'factor')
    out.push({
      t: n,
      step: {
        relation: goal === 'compute' ? 'evaluation' : 'identity',
        move: goal === 'compute' ? 'compute' : 'simplify',
      },
      cost: 4,
    });
  return out;
}

function ruleOf(ctx: Ctx, id: string): { ruleId?: string } {
  const r = ctx.rules.find((x) => x.id === id);
  return r ? { ruleId: r.qid } : {};
}

/** Facteur commun d'une somme : pgcd des coefficients × monôme commun ; null s'il vaut 1. */
function factorOut(t: Term, sorts: Sorts): Term | null {
  if (t.k !== 'add') return null;
  const n = normalize(t, { sorts });
  if (!n.ok || polyRat(n.rf.d) === null || !Q.eq(polyRat(n.rf.d)!, Q.ONE)) return null;
  const p = n.rf.n;
  if (p.t.size < 2) return null;
  const content = polyContent(p);
  const terms = [...p.t.values()];
  // monôme commun (lettres seulement)
  const common = new Map<string, { a: Term; e: number }>();
  terms[0]!.m.forEach((f) => f.a.kind === 'sym' && common.set(f.a.key, { a: f.a.term, e: f.e }));
  for (const x of terms.slice(1))
    for (const [k, v] of [...common]) {
      const g = x.m.find((f) => f.a.key === k);
      if (!g) common.delete(k);
      else v.e = Math.min(v.e, g.e);
    }
  const sign = Q.sign(polyTerms(p)[0]!.c) < 0 ? -1 : 1;
  const k = Q.mul(content, Q.rat(sign));
  const mono: Term[] = [...common.values()].map((v) => (v.e === 1 ? v.a : pow(v.a, num(v.e))));
  if (Q.eq(k, Q.ONE) && !mono.length) return null;
  const factor = canon(mul(num(k), ...mono), sorts);
  const rest = nf(div(t, factor), sorts);
  if (!rest || rest.k !== 'add') return null;
  return canon(mul(factor, rest), sorts);
}

/** Produit nul : A × B = 0 ⟺ A = 0 ou B = 0. */
function zeroProduct(r: Relation): Relation[] | null {
  if (r.rel !== '=') return null;
  const [p, z] =
    r.r.k === 'num' && Q.isZero(r.r.v)
      ? [r.l, r.r]
      : r.l.k === 'num' && Q.isZero(r.l.v)
        ? [r.r, r.l]
        : [null, null];
  if (!p || !z || p.k !== 'mul') return null;
  const factors = p.c.filter((c) => symbolsOf(c).size > 0);
  if (factors.length < 2) return null;
  return factors.map((f) => ({ rel: '=' as Rel, l: f, r: num(0) }));
}

function moves(s: Statement, ctx: Ctx): Move[] {
  const out: Move[] = [];
  if (s.k === 'expr') {
    for (const m of expressionMoves(s.e, ctx, ctx.goal.kind))
      out.push({ s: { k: 'expr', e: m.t }, step: m.step, cost: m.cost });
    return out;
  }
  // un ensemble (moteur v23) : pas de coup automatique, le corrigé vient du modèle
  if (s.k === 'set') return out;
  const items: Relation[] = s.k === 'rel' ? [{ rel: s.rel, l: s.l, r: s.r }] : s.items;
  const rebuild = (i: number, rs: Relation[]): Statement => {
    const all = [...items.slice(0, i), ...rs, ...items.slice(i + 1)];
    return all.length === 1 ? { k: 'rel', ...all[0]! } : { k: 'or', items: all };
  };
  items.forEach((r, i) => {
    if (ctx.x && r.l.k === 'sym' && r.l.n === ctx.x && !has(r.r, ctx.x)) {
      // déjà isolé : seulement réduire le second membre
      const n = nf(r.r, ctx.sorts);
      if (n && termKey(n) !== termKey(r.r))
        out.push({
          s: rebuild(i, [{ ...r, r: n }]),
          step: { relation: 'equivalent', move: 'simplify' },
          cost: 1,
        });
      return;
    }
    const zp = zeroProduct(r);
    if (zp)
      out.push({
        s: rebuild(i, zp),
        step: { relation: 'equivalent', move: 'zero_product' },
        cost: 1,
      });
    for (const m of relationMoves(r, ctx))
      out.push({ s: rebuild(i, [m.r]), step: m.step, cost: m.cost });
    // identités sur un membre (factoriser pour résoudre)
    for (const side of ['l', 'r'] as const)
      for (const m of expressionMoves(
        r[side],
        ctx,
        ctx.goal.kind === 'isolate_variable' ? 'solve' : ctx.goal.kind,
      ))
        if (m.step.ruleId)
          out.push({
            s: rebuild(i, [{ ...r, [side]: m.t }]),
            step: { ...m.step, relation: 'equivalent' },
            cost: m.cost + 1,
          });
    // échanger les membres
    if (r.rel === '=' && ctx.x && !has(r.l, ctx.x) && has(r.r, ctx.x))
      out.push({
        s: rebuild(i, [{ rel: '=', l: r.r, r: r.l }]),
        step: { relation: 'equivalent', move: 'swap_sides' },
        cost: 1,
      });
  });
  return out;
}

/* ------------------------------ recherche ------------------------------ */

function unknownOf(s: Statement, goal: Goal): string | null {
  if (goal.target) return goal.target;
  const names = [...statementSymbols(s)];
  if (names.length === 1) return names[0]!;
  return names.includes('x') ? 'x' : null;
}

/** Cherche un chemin de `start` vers l'objectif, sous budget. */
export function searchAlgebra(start: string, opts: SearchOptions): SearchResult {
  const p = parseStatement(start);
  if (!p.ok) return { status: 'unsupported', code: p.reason };
  if (p.s.k === 'set') return { status: 'unsupported', code: 'set_statement' };
  return run(p.s, opts, opts.budget.branches > 0);
}

function run(s0: Statement, opts: SearchOptions, allowBranches: boolean): SearchResult {
  const now = opts.now ?? (() => Date.now());
  const hyps = [...(opts.hypotheses ?? [])];
  const sorts = opts.sorts ?? {};
  const ctx: Ctx = {
    rules: opts.rules,
    goal: opts.goal,
    x: s0.k === 'expr' ? null : unknownOf(s0, opts.goal),
    facts: factsFrom(hyps, sorts),
    hyps,
    sorts,
    deadline: now() + opts.budget.ms,
    now,
    wantsBranch: new Set(),
  };
  if (s0.k !== 'expr' && !ctx.x) return { status: 'unsupported', code: 'no_unknown' };
  // lettres supposées nulles (cas « a = 0 ») : remplacées par 0
  const zeros: Record<string, Term> = {};
  for (const [name, set] of ctx.facts.sign)
    if (set.zero && !set.pos && !set.neg) zeros[name] = num(0);
  if (Object.keys(zeros).length) s0 = mapStatement(s0, (t) => substitute(t, zeros));
  const keyOf = (s: Statement): string =>
    statementKey(ctx.goal.kind === 'compute' ? s : canonStatement(s, sorts));
  const start: Node = {
    s: s0,
    key: keyOf(s0),
    g: 0,
    h: heuristic(s0, ctx),
    depth: 0,
    steps: [],
    conditions: [],
  };
  const open: Node[] = [start];
  const seen = new Map<string, number>([[start.key, 0]]);
  let nodes = 0;
  while (open.length) {
    if (now() > ctx.deadline) return { status: 'budget_exhausted', nodes, reason: 'ms' };
    if (nodes >= opts.budget.nodes) return { status: 'budget_exhausted', nodes, reason: 'nodes' };
    // meilleur d'abord (départage stable : profondeur, puis clé)
    let bi = 0;
    for (let i = 1; i < open.length; i++) {
      const a = open[i]!;
      const b = open[bi]!;
      const fa = a.g + a.h;
      const fb = b.g + b.h;
      if (fa < fb || (fa === fb && (a.h < b.h || (a.h === b.h && a.key < b.key)))) bi = i;
    }
    const cur = open.splice(bi, 1)[0]!;
    nodes++;
    if (isGoal(cur.s, ctx))
      return {
        status: 'found',
        steps: cur.steps,
        final: statementText(cur.s),
        conditions: cur.conditions,
        nodes,
      };
    if (cur.depth >= opts.budget.depth) continue;
    for (const m of moves(cur.s, ctx)) {
      const key = keyOf(m.s);
      const g = cur.g + m.cost;
      const old = seen.get(key);
      if (old !== undefined && old <= g) continue;
      seen.set(key, g);
      open.push({
        s: m.s,
        key,
        g,
        h: heuristic(m.s, ctx),
        depth: cur.depth + 1,
        steps: [...cur.steps, { from: statementText(cur.s), to: statementText(m.s), ...m.step }],
        conditions: [...cur.conditions, ...(m.step.conditions ?? [])],
      });
    }
  }
  // rien trouvé : des coups attendaient le signe d'un paramètre → un cas par signe
  if (allowBranches && ctx.wantsBranch.size) {
    const term = [...ctx.wantsBranch].sort()[0]!;
    const t = parseStatement(term);
    const v = t.ok && t.s.k === 'expr' ? branchVar(t.s.e) : null;
    if (v) {
      const branches: { when: Condition[]; result: SearchResult }[] = [];
      for (const kind of ['positive', 'negative', 'zero'] as const) {
        const when = conditionOn(kind, v);
        if (decide(when, ctx.facts) === 'false') continue;
        const left = Math.max(0, ctx.deadline - now());
        branches.push({
          when: [when],
          result: run(
            s0,
            { ...opts, hypotheses: [...hyps, when], budget: { ...opts.budget, ms: left } },
            false,
          ),
        });
      }
      return { status: 'branches', branches, nodes };
    }
  }
  return { status: 'not_found', nodes };
}

function branchVar(t: Term): Term | null {
  const syms = [...symbolsOf(t)];
  return syms.length === 1 ? { k: 'sym', n: syms[0]! } : t;
}

function canonStatement(s: Statement, sorts: Sorts): Statement {
  return mapStatement(s, (t) => canon(t, sorts));
}
